type CSSStyleValue = string | number | undefined;

type CSSStyleObject = {
  [key: string]: CSSStyleValue | CSSStyleObject;
};

type StyleLike = Record<string, unknown>;

type VariantConfig = Record<string, Record<string, StyleLike> | undefined>;

type VariantSelection<V extends VariantConfig> = {
  [K in keyof V]?: keyof NonNullable<V[K]> | boolean;
};

type CompoundVariant<V extends VariantConfig> = Partial<{
  [K in keyof V]: keyof NonNullable<V[K]> | boolean;
}> & { styles: CSSStyleObject };

type SVAConfig<V extends VariantConfig> = {
  variants: V;
  compoundVariants?: CompoundVariant<V>[];
  defaultVariants?: VariantSelection<V>;
};

/**
 * Combine two style objects by replacing nested selector bodies wholesale.
 *
 * Top-level CSS properties are merged, but when a key's value is itself an
 * object (e.g. `'&:hover'`, `'&:focus-within'`, `'@media (...)'`) the source
 * object replaces the target object completely rather than being recursively
 * merged. Variants that re-declare a pseudo-selector therefore own that state
 * entirely — no properties leak in from the base or earlier variants.
 */
function mergeStyles(
  target: CSSStyleObject,
  source: CSSStyleObject,
): CSSStyleObject {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    const sourceValue = source[key];
    if (sourceValue === undefined) continue;
    result[key] = sourceValue;
  }
  return result;
}

const isBooleanVariantConfig = (variantConfig: Record<string, StyleLike>) =>
  'true' in variantConfig || 'false' in variantConfig;

/**
 * Style Variance Authority — CVA-inspired helper that outputs CSS style objects
 * instead of class name strings. Designed to work with Alloy token trees.
 *
 * Merge semantics: top-level CSS properties combine across base + variants +
 * compound variants, but nested selector bodies (`'&:hover'`, `'&:active'`,
 * `'@media ...'`, etc.) are replaced wholesale by the last source that sets
 * them. A variant re-declaring a pseudo-selector owns that state entirely —
 * properties from the base or earlier variants do not leak through.
 *
 * Boolean variants (cells keyed `true`/`false`) treat a missing or undefined
 * prop value as `false`, mirroring how an unset boolean prop reads in JSX
 * (`<X disabled />` vs `<X />`). `defaultVariants` still wins when set.
 * Categorical variants (string-keyed cells) skip when undefined — there's no
 * meaningful default unless `defaultVariants` provides one.
 *
 * @example
 * ```ts
 * const textareaStyles = sva(
 *   { display: 'flex', 'font-family': 'Arial' },
 *   {
 *     variants: {
 *       state: {
 *         idle: { 'background-color': 'transparent', cursor: 'text' },
 *         error: { 'background-color': '#fff0f0', 'border-color': 'red' },
 *         disabled: { cursor: 'no-drop', opacity: 0.5 },
 *       },
 *       size: {
 *         small: { height: 32 },
 *         medium: { height: 40 },
 *       },
 *     },
 *     compoundVariants: [
 *       { state: 'error', size: 'small', styles: { 'border-width': 2 } },
 *     ],
 *     defaultVariants: { state: 'idle', size: 'medium' },
 *   },
 * );
 *
 * textareaStyles();
 * // → { display: 'flex', 'font-family': 'Arial', 'background-color': 'transparent', cursor: 'text', height: 40 }
 *
 * textareaStyles({ state: 'error', size: 'small' });
 * // → { display: 'flex', 'font-family': 'Arial', 'background-color': '#fff0f0', 'border-color': 'red', height: 32, 'border-width': 2 }
 * ```
 */
export function sva<V extends VariantConfig>(
  base: StyleLike | undefined,
  config: SVAConfig<V>,
) {
  return (props?: VariantSelection<V>): CSSStyleObject => {
    const filtered = props
      ? Object.fromEntries(
          Object.entries(props).filter(([, v]) => v !== undefined),
        )
      : {};
    const resolved: Record<string, unknown> = {
      ...config.defaultVariants,
      ...filtered,
    };

    let result = mergeStyles({}, (base ?? {}) as CSSStyleObject);

    for (const variantKey of Object.keys(config.variants)) {
      const variantConfig = config.variants[variantKey];
      if (!variantConfig) continue;

      const resolvedValue = resolved[variantKey];
      let key: string | undefined;

      if (resolvedValue === null || resolvedValue === undefined) {
        if (isBooleanVariantConfig(variantConfig)) {
          key = 'false';
        } else {
          continue;
        }
      } else {
        key =
          typeof resolvedValue === 'boolean'
            ? String(resolvedValue)
            : (resolvedValue as string);
      }

      const variantStyles = variantConfig[key];
      if (variantStyles) {
        result = mergeStyles(result, variantStyles as CSSStyleObject);
      }
    }

    if (config.compoundVariants) {
      const normalize = (v: unknown, variantKey: keyof V) => {
        if (v === undefined || v === null) {
          const variantConfig = config.variants[variantKey];
          if (variantConfig && isBooleanVariantConfig(variantConfig)) {
            return 'false';
          }
        }
        return typeof v === 'boolean' ? String(v) : v;
      };
      for (const compound of config.compoundVariants) {
        const variantKeys = Object.keys(config.variants) as (keyof V)[];
        const matches = variantKeys
          .filter((key) => key in compound)
          .every(
            (key) =>
              normalize(compound[key], key) ===
              normalize(resolved[key as string], key),
          );
        if (matches && compound.styles) {
          result = mergeStyles(result, compound.styles);
        }
      }
    }

    return result;
  };
}

export type { CSSStyleObject, SVAConfig, VariantSelection, CompoundVariant };
